import React, { useState } from 'react';
import { Section, Card } from '../shared/primitives';
import OOORProcedures from './OOORProcedures';
import { ExternalLink } from 'lucide-react';
import { caseDocumentation, EPIC_TRANSITION_NOTE, MEDICAL_DIRECTION, ORDERS } from '../../../data/hospitals/thiEpic';

/*
 * Epic at THI, in three tabs by task:
 *   Documentation  – what to chart, by Epic's PreProcedure / IntraProcedure / PostProcedure tabs,
 *                    plus Out-of-OR for airways, codes and other procedures outside the OR
 *   Orders & Templates – which ordersets and note templates to use, by situation (content pending)
 *   Attendings     – Medical Direction requirements and the SmartPhrases attendings add by hand
 */

const TABS = [
  { id: 'documentation', label: 'Documentation' },
  { id: 'orders', label: 'Orders & Templates' },
  { id: 'attendings', label: 'Attendings' },
];
const PHASES = [
  { id: 'pre', label: 'Pre' },
  { id: 'intra', label: 'Intra' },
  { id: 'post', label: 'Post' },
  { id: 'ooor', label: 'Out-of-OR' },
];
const PHASE_TITLES = { pre: 'PreProcedure tab', intra: 'IntraProcedure tab', post: 'PostProcedure tab' };
const TAB_KEY = 'epic-tab';
const PHASE_KEY = 'epic-phase';
// Older entry points and saved tabs used these ids
const LEGACY = { 'case-docs': { tab: 'documentation' }, 'medical-direction': { tab: 'attendings' }, ooor: { tab: 'documentation', phase: 'ooor' } };
const resolve = (id) => (id ? LEGACY[id] || { tab: TABS.some((t) => t.id === id) ? id : TABS[0].id } : null);

const Segmented = ({ items, value, onChange, small }) => (
  <div className={`bg-gray-100 dark:bg-gray-800 rounded-xl p-1 grid gap-1 grid-cols-${items.length}`} role="tablist">
    {items.map((t) => (
      <button
        key={t.id}
        role="tab"
        aria-selected={value === t.id}
        onClick={() => { onChange(t.id); }}
        className={`${small ? 'py-1.5 text-xs' : 'py-2 text-xs'} px-1 rounded-lg font-semibold leading-tight transition-all ${
          value === t.id ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'
        }`}
      >
        {t.label}
      </button>
    ))}
  </div>
);

const Tag = ({ children }) => (
  <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
    {children}
  </span>
);

const StepRow = ({ n, step }) => (
  <div className="px-4 py-3 flex gap-3">
    <span
      className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
      style={{ backgroundColor: 'rgba(159, 141, 112, 0.14)', color: '#8a7a61' }}
    >
      {n}
    </span>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[15px] font-medium text-gray-900 dark:text-white">{step.title}</span>
        {step.who === 'attending' && <Tag>Attendings</Tag>}
        {step.optional && <Tag>Optional</Tag>}
      </div>
      {step.details && (
        <ul className="mt-1 space-y-0.5">
          {step.details.map((d) => (
            <li key={d} className="text-sm text-gray-600 dark:text-gray-300 leading-snug pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[0.55em] before:w-1 before:h-1 before:rounded-full before:bg-gray-400">
              {d}
            </li>
          ))}
        </ul>
      )}
      {step.footnote && (
        <div className="mt-1.5 flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400 leading-snug">
          {step.footnote.who === 'attending' && <Tag>Attendings</Tag>}
          <span>{step.footnote.text}</span>
        </div>
      )}
    </div>
  </div>
);

const Documentation = ({ initialPhase }) => {
  const [phase, setPhaseState] = useState(() => {
    if (initialPhase) return initialPhase;
    try { return PHASES.some((p) => p.id === localStorage.getItem(PHASE_KEY)) ? localStorage.getItem(PHASE_KEY) : 'pre'; } catch { return 'pre'; }
  });
  const setPhase = (id) => {
    setPhaseState(id);
    try { localStorage.setItem(PHASE_KEY, id); } catch { /* ignore */ }
  };
  return (
    <div className="space-y-5">
      <Segmented items={PHASES} value={phase} onChange={setPhase} small />
      {phase === 'ooor' ? (
        <OOORProcedures />
      ) : (
        <>
          <Section title={PHASE_TITLES[phase]}>
            <Card>
              {caseDocumentation[phase].map((step, i) => <StepRow key={step.title} n={i + 1} step={step} />)}
            </Card>
          </Section>
          <p className="px-1 text-xs text-gray-400 dark:text-gray-500 leading-snug">{EPIC_TRANSITION_NOTE}</p>
        </>
      )}
    </div>
  );
};

const NameRow = ({ item }) => (
  <div className="px-4 py-3">
    <div className="text-[15px] font-medium text-gray-900 dark:text-white leading-snug">{item.name}</div>
    {item.note && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.note}</div>}
  </div>
);

const Orders = () => (
  <div className="space-y-5">
    <Section title="Common orders">
      <Card>{ORDERS.common.map((o) => <NameRow key={o.name} item={o} />)}</Card>
    </Section>
    <Section title="Ordersets">
      <Card>{ORDERS.ordersets.map((o) => <NameRow key={o.name} item={o} />)}</Card>
    </Section>
    <Section title="Templates">
      <Card><p className="px-4 py-3 text-sm text-gray-400 dark:text-gray-500 italic">{ORDERS.templatesPending}</p></Card>
    </Section>
  </div>
);

const MedicalDirection = () => {
  const md = MEDICAL_DIRECTION;
  return (
    <div className="space-y-5">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-[#9F8D70]/30 dark:border-[#9F8D70]/50 shadow-card overflow-hidden">
        <div className="px-4 py-3 bg-[#9F8D70]/10 dark:bg-[#9F8D70]/20 flex items-center justify-between">
          <span className="font-semibold text-[#8a7a61] dark:text-[#C4B5A0]">Medical direction at THI</span>
          <Tag>Attendings</Tag>
        </div>
        <p className="p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{md.intro}</p>
      </div>

      <Section title="SmartPhrases">
        <Card>
          {md.smartPhrases.length === 0 ? (
            <div className="px-4 py-4 text-sm text-gray-400 dark:text-gray-500 italic">List of medical direction SmartPhrases: coming.</div>
          ) : md.smartPhrases.map((sp) => (
            <div key={sp.phrase || sp.use} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="text-[15px] font-medium text-gray-900 dark:text-white leading-snug">{sp.use}</div>
                {sp.pending ? (
                  <span className="flex-shrink-0 mt-0.5 text-xs italic text-gray-400 dark:text-gray-500">coming</span>
                ) : (
                  <code className="flex-shrink-0 px-2 py-1 rounded-md text-[13px] font-mono bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white">{sp.phrase}</code>
                )}
              </div>
              {sp.phase && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{sp.phase}</div>}
              {sp.note && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-snug">{sp.note}</div>}
            </div>
          ))}
        </Card>
      </Section>

      <Section title="What the phrases attest to · 42 CFR 415.110">
        <Card>
          {md.elements.map((text, i) => (
            <div key={text} className="px-4 py-3 flex gap-3">
              <span className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(159, 141, 112, 0.14)', color: '#8a7a61' }}>{i + 1}</span>
              <span className="text-[15px] text-gray-900 dark:text-white leading-snug">{text}</span>
            </div>
          ))}
        </Card>
      </Section>

      <div className="px-1 space-y-1.5">
        <p className="text-xs text-gray-400 dark:text-gray-500 leading-snug">
          Concurrency limits, permitted activities and billing detail live under Department Resources.
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {md.references.map((r) => (
            <a key={r.url} href={r.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 underline-offset-2 hover:underline">
              {r.label} <ExternalLink size={11} />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

const EpicGuide = ({ initialTab }) => {
  const start = resolve(initialTab);
  const [tab, setTabState] = useState(() => {
    if (start) return start.tab;
    try { return resolve(localStorage.getItem(TAB_KEY))?.tab || TABS[0].id; } catch { return TABS[0].id; }
  });
  const setTab = (id) => {
    setTabState(id);
    try { localStorage.setItem(TAB_KEY, id); } catch { /* ignore */ }
  };
  return (
    <div className="space-y-5">
      <Segmented items={TABS} value={tab} onChange={setTab} />
      {tab === 'documentation' && <Documentation initialPhase={start?.phase} />}
      {tab === 'orders' && <Orders />}
      {tab === 'attendings' && <MedicalDirection />}
    </div>
  );
};

export default EpicGuide;
